// rewards.js - API functions for rewards management
import { db } from '../firebaseConfig';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';

const REWARDS_COLLECTION = 'rewards';
const REDEMPTIONS_COLLECTION = 'redemptions';

/**
 * Get all rewards
 */
export const getRewards = async () => {
  try {
    const rewardsRef = collection(db, REWARDS_COLLECTION);
    const q = query(rewardsRef, orderBy('requiredTier', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching rewards:', error);
    throw error;
  }
};

/**
 * Get a single reward by ID
 */
export const getRewardById = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (rewardDoc.exists()) {
      return {
        id: rewardDoc.id,
        ...rewardDoc.data()
      };
    } else {
      throw new Error('Reward not found');
    }
  } catch (error) {
    console.error('Error fetching reward:', error);
    throw error;
  }
};

/**
 * Get active rewards only
 */
export const getActiveRewards = async () => {
  try {
    const rewardsRef = collection(db, REWARDS_COLLECTION);
    const q = query(
      rewardsRef, 
      where('active', '==', true),
      orderBy('requiredTier', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching active rewards:', error);
    throw error;
  }
};

/**
 * Create a new reward (Admin only)
 */
export const createReward = async (rewardData) => {
  try {
    const rewardRef = collection(db, REWARDS_COLLECTION);
    const newReward = {
      ...rewardData,
      active: true,
      createdAt: serverTimestamp(),
      redeemedCount: 0
    };
    
    const docRef = await addDoc(rewardRef, newReward);
    return docRef.id;
  } catch (error) {
    console.error('Error creating reward:', error);
    throw error;
  }
};

/**
 * Update a reward (Admin only)
 */
export const updateReward = async (rewardId, updates) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    await updateDoc(rewardRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating reward:', error);
    throw error;
  }
};

/**
 * Delete a reward (Admin only)
 */
export const deleteReward = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    await deleteDoc(rewardRef);
  } catch (error) {
    console.error('Error deleting reward:', error);
    throw error;
  }
};

/**
 * Redeem a reward (user action)
 */
export const redeemReward = async (userId, rewardId, userTier) => {
  try {
    // Get reward details
    const reward = await getRewardById(rewardId);
    
    // Check if user has required tier
    if (userTier < reward.requiredTier) {
      throw new Error('Insufficient tier level');
    }
    
    // Check if reward is active
    if (!reward.active) {
      throw new Error('This reward is no longer available');
    }
    
    // Update user's redeemed rewards (this would be handled in users.js)
    // For now, return success
    return {
      success: true,
      reward,
      tierRequired: reward.requiredTier
    };
  } catch (error) {
    console.error('Error redeeming reward:', error);
    throw error;
  }
};

/**
 * Get rewards by category
 */
export const getRewardsByCategory = async (category) => {
  try {
    const rewardsRef = collection(db, REWARDS_COLLECTION);
    const q = query(
      rewardsRef,
      where('category', '==', category),
      where('active', '==', true),
      orderBy('requiredTier', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching rewards by category:', error);
    throw error;
  }
};

/**
 * Increment redeem count for a reward
 */
export const incrementRedeemCount = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (rewardDoc.exists()) {
      const currentCount = rewardDoc.data().redeemedCount || 0;
      await updateDoc(rewardRef, {
        redeemedCount: currentCount + 1,
        lastRedeemedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error incrementing redeem count:', error);
    throw error;
  }
};

/**
 * Create a redemption record with QR code data
 */
export const createRedemption = async (userId, rewardId, userData, rewardData) => {
  try {
    const redemptionRef = collection(db, REDEMPTIONS_COLLECTION);
    
    const redemptionData = {
      userId,
      rewardId,
      user: {
        firstName: userData.firstName,
        lastName: userData.lastName,
        title: userData.title,
        membershipID: userData.membershipID,
        tier: userData.tier || 1
      },
      reward: {
        merchantName: rewardData.merchantName,
        title: rewardData.title,
        discount: rewardData.discount,
        terms: rewardData.terms,
        category: rewardData.category
      },
      redeemedAt: serverTimestamp(),
      used: false,
      expiresAt: new Date(Date.now() + (24 * 60 * 60 * 1000)) // 24 hours from now
    };
    
    const docRef = await addDoc(redemptionRef, redemptionData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating redemption:', error);
    throw error;
  }
};

/**
 * Get redemption details by ID
 */
export const getRedemptionDetails = async (redemptionId) => {
  try {
    const redemptionRef = doc(db, REDEMPTIONS_COLLECTION, redemptionId);
    const redemptionDoc = await getDoc(redemptionRef);
    
    if (!redemptionDoc.exists()) {
      throw new Error('Redemption not found');
    }
    
    return {
      id: redemptionDoc.id,
      ...redemptionDoc.data()
    };
  } catch (error) {
    console.error('Error fetching redemption details:', error);
    throw error;
  }
};

/**
 * Mark redemption as used
 */
export const markRedemptionAsUsed = async (redemptionId) => {
  try {
    const redemptionRef = doc(db, REDEMPTIONS_COLLECTION, redemptionId);
    await updateDoc(redemptionRef, {
      used: true,
      usedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking redemption as used:', error);
    throw error;
  }
};

/**
 * Get user's redemptions
 */
export const getUserRedemptions = async (userId) => {
  try {
    const redemptionsRef = collection(db, REDEMPTIONS_COLLECTION);
    const q = query(
      redemptionsRef,
      where('userId', '==', userId),
      orderBy('redeemedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user redemptions:', error);
    throw error;
  }
};

